import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Minus, Plus, Trash2, ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import namkeFallback from '../assets/namke-fallback.png';

const PRIMARY = 'rgb(5, 70, 37)';

const itemTotal = (item) => {
  const supplementsTotal = (item.supplements || []).reduce(
    (sum, sup) => sum + (parseFloat(sup.price) || 0) * (parseInt(sup.quantity) || 1),
    0
  );
  return ((parseFloat(item.price) || 0) + supplementsTotal) * (parseInt(item.quantity) || 1);
};

const CartSidebar = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { cartItems, updateQuantity, removeFromCart, deleteFromCart } = useCart();
  const navigate = useNavigate();

  // Empêcher le scroll de la page derrière quand le tiroir est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  const subtotal = cartItems.reduce((sum, item) => sum + itemTotal(item), 0);

  const handleGoToCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    onClose();
    navigate('/');
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[99] ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panneau */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-[420px] shadow-2xl transform transition-transform duration-300 ease-in-out z-[101] flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ background: '#FCFBF5' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between shrink-0" style={{ padding: '18px 20px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
          <button
            onClick={onClose}
            style={{ border: 'none', cursor: 'pointer', background: 'transparent', width: '38px', height: '38px', borderRadius: '999px', display: 'grid', placeItems: 'center', color: PRIMARY }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            aria-label={t('cartSidebar.close')}
          >
            <X size={19} />
          </button>
          <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '18px' }}>{t('cartSidebar.title')}</span>
          <div style={{ width: '38px' }} />
        </div>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <ShoppingCart size={56} style={{ color: 'rgba(5,16,4,0.16)', marginBottom: '16px' }} />
            <h2 style={{ margin: '0 0 8px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '19px' }}>{t('cartSidebar.emptyTitle')}</h2>
            <p style={{ margin: '0 0 22px', fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>
              {t('cartSidebar.emptySubtitle')}
            </p>
            <button
              onClick={handleContinueShopping}
              style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', padding: '13px 26px', borderRadius: '999px' }}
            >
              {t('cartSidebar.continueShopping')}
            </button>
          </div>
        ) : (
          <>
            {/* Articles */}
            <div className="flex-1 overflow-y-auto" style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px' }}>
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-start gap-3" style={{ padding: '12px', borderRadius: '20px' }}>
                  <img
                    src={item.image || namkeFallback}
                    alt={item.name}
                    className="shrink-0"
                    style={{ width: '64px', height: '64px', borderRadius: '16px', objectFit: 'cover', background: '#FBF9D9' }}
                    onError={(e) => { e.target.src = namkeFallback; }}
                  />
                  <div className="flex-1 min-w-0">
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '14.5px', color: '#051004' }} className="line-clamp-2">{item.name}</p>
                    {item.restaurantName && (
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }} className="truncate">{item.restaurantName}</p>
                    )}
                    {item.supplements && item.supplements.length > 0 && (
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }} className="line-clamp-1">
                        {item.supplements.map((s) => s.name).join(', ')}
                      </p>
                    )}
                    <p style={{ margin: '4px 0 0', fontWeight: 700, fontSize: '14px' }}>{itemTotal(item).toFixed(2)} €</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" style={{ background: '#FBF9D9', borderRadius: '999px', padding: '4px' }}>
                    <button
                      type="button"
                      onClick={() => (item.quantity <= 1 ? deleteFromCart(item) : removeFromCart(item))}
                      style={{ border: 'none', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '999px', background: '#FCFBF5', color: PRIMARY, display: 'grid', placeItems: 'center' }}
                      aria-label={item.quantity <= 1 ? t('cartSidebar.remove') : t('cartSidebar.decreaseQuantity')}
                    >
                      {item.quantity <= 1 ? <Trash2 size={13} /> : <Minus size={13} />}
                    </button>
                    <span style={{ fontSize: '13.5px', fontWeight: 600, width: '18px', textAlign: 'center' }}>{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      style={{ border: 'none', cursor: 'pointer', width: '28px', height: '28px', borderRadius: '999px', background: PRIMARY, color: '#FBF9D9', display: 'grid', placeItems: 'center' }}
                      aria-label={t('cartSidebar.increaseQuantity')}
                    >
                      <Plus size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="shrink-0" style={{ borderTop: '1px solid rgba(5,16,4,0.08)', padding: '18px 20px' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '14.5px', color: 'rgba(5,16,4,0.7)' }}>{t('cartSidebar.subtotal')}</span>
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '20px' }}>{subtotal.toFixed(2)} €</strong>
              </div>
              <button
                type="button"
                onClick={handleGoToCheckout}
                style={{ width: '100%', border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15.5px', padding: '15px 0', borderRadius: '999px' }}
              >
                {t('cartSidebar.checkout')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CartSidebar;
